import { lazy, Suspense } from 'react';
import { createBrowserRouter, createRoutesFromChildren, Navigate, Route, useLocation } from 'react-router-dom';
import ForgotPassword from '@Auth/ForgotPassword';
import Login from '@Auth/Login';
import Logout from '@Auth/Logout';
import PasswordForm from '@Auth/PasswordForm';
import { PrivateRoutes } from '@Auth/PrivateRoutes';
import { ProtectedRoutes } from '@Auth/ProtectedRoutes';
import { PublicRoutes } from '@Auth/PublicRoutes';
import { RootRedirect } from '@Auth/RootRedirect';
import { RedirectSubmissionsLegacy } from '@components/RedirectSubmissionsLegacy';
import { ContractBuilderLoaderController } from '@ContractBuilder/modules/contract-builder-loader';
import { ErrorController } from '@features/error';
import { SentryErrorElement } from '@pages/ErrorPage';
import {
  UserBlocksPage,
  UserBrandingPage,
  UserClausesPage,
  UserEvalConfigPage,
  UserEvalConfigsPage,
  UserTagsPage,
  UserTemplatesPage,
  UserUsersPage,
} from '@pages/index';
import { UserSingleBlockPage } from '@pages/User/BlockEdit';
import { UserPreDraftContractPage } from '@pages/User/PreDraftContract';
import { SubmissionsRouterPage } from '@pages/User/SubmissionsRouter';
import { UserRole } from '@root/@types/types';
import { ADMIN_ROLES } from '@root/helpers/permissions/utils';
import { wrapCreateBrowserRouterV6 } from '@sentry/react';

import Nav from '../routes';

// ContractBuilder: 62 kB gzip. ApiDocs: 44 kB + pulls in redoc (451 kB gzip) on demand.
const ContractBuilderPage = lazy(() => import('@pages/ContractBuilder'));
const UserApiDocsPage = lazy(() => import('@pages/User/ApiDocs').then((m) => ({ default: m.UserApiDocsPage })));

const ContractBuilderRoute = () => (
  <Suspense fallback={<ContractBuilderLoaderController />}>
    <ContractBuilderPage />
  </Suspense>
);

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV6(createBrowserRouter);

const ALL_ROLES_EXCEPT_BROKERS_AND_TEMPLATE_MANAGERS = [UserRole.Admin, UserRole.Superadmin, UserRole.User];
const ALL_ROLES_EXCEPT_TEMPLATE_MANAGERS = [UserRole.Admin, UserRole.Superadmin, UserRole.User, UserRole.Broker];

// Component to capture original URL before 404 redirect
const RedirectTo404 = () => {
  const location = useLocation();
  const originalUrl = encodeURIComponent(location.pathname + location.search);

  return <Navigate to={`/404?from=${originalUrl}`} replace />;
};

export const getAppRouter = (
  isUsersEnabled?: boolean,
  isBrandingEnabled?: boolean,
  isPreDraftContractEnabled?: boolean,
  isEvalConfigsEnabled?: boolean,
  isApiDocsEnabled?: boolean,
) =>
  sentryCreateBrowserRouter(
    createRoutesFromChildren(
      <Route errorElement={<SentryErrorElement />}>
        {/* PUBLIC */}
        <Route element={<PublicRoutes />}>
          <Route path={Nav.Register} element={<PasswordForm />} />
          <Route path={Nav.Login} element={<Login />} />
          <Route path={Nav.Auth} element={<Login />} />
          <Route path={Nav.Logout} element={<Logout />} />
          <Route path={Nav.ForgotPassword} element={<ForgotPassword />} />
          <Route path={Nav.ResetPassword} element={<PasswordForm />} />
          <Route path="/404" element={<ErrorController type="404" />} />
          <Route path="*" element={<RedirectTo404 />} />
        </Route>
        {/* PRIVATE */}
        <Route element={<PrivateRoutes />}>
          {/* Root: Cognito SSO landing point. Auth-gated here so it fires after Amplify settles. */}
          <Route path={Nav.Home} element={<RootRedirect />} />
          {/* WITH MENU */}
          <Route path={Nav.SubmissionsLegacy} element={<RedirectSubmissionsLegacy />} />
          <Route
            element={
              <ProtectedRoutes
                authorizedRoles={ALL_ROLES_EXCEPT_BROKERS_AND_TEMPLATE_MANAGERS}
                fallbackPath={Nav.NotFound}
              />
            }
          >
            <Route path={Nav.Submissions} element={<SubmissionsRouterPage />} />
          </Route>
          <Route element={<ProtectedRoutes authorizedRoles={ADMIN_ROLES} />}>
            <Route path={Nav.Block} element={<UserSingleBlockPage />} />
            <Route path={Nav.Templates} element={<UserTemplatesPage />} />
            <Route path={Nav.Blocks} element={<UserBlocksPage />} />
            <Route path={Nav.Clause} element={<UserSingleBlockPage />} />
            <Route path={Nav.Clauses} element={<UserClausesPage />} />
            <Route path={Nav.Tags} element={<UserTagsPage />} />
            {isBrandingEnabled && <Route path={Nav.Branding + '/*'} element={<UserBrandingPage />} />}
            {isApiDocsEnabled && <Route path={Nav.ApiDocs} element={<UserApiDocsPage />} />}
          </Route>
          <Route element={<ProtectedRoutes authorizedRoles={[UserRole.Superadmin]} />}>
            {isUsersEnabled && <Route path={Nav.Users} element={<UserUsersPage />} />}
          </Route>
          {isEvalConfigsEnabled && (
            <Route element={<ProtectedRoutes authorizedRoles={[UserRole.Superadmin]} />}>
              <Route path={Nav.EvalConfigs} element={<UserEvalConfigsPage />} />
              <Route path={Nav.EvalConfig} element={<UserEvalConfigPage />} />
            </Route>
          )}
          <Route element={<ProtectedRoutes authorizedRoles={[UserRole.Broker, UserRole.TemplateManager]} />}>
            {isPreDraftContractEnabled && <Route path={Nav.PreDraftContract} element={<UserPreDraftContractPage />} />}
          </Route>
          {/* WITH MENU END */}
          {/* CONTRACT BUILDER */}
          <Route element={<ProtectedRoutes authorizedRoles={ALL_ROLES_EXCEPT_TEMPLATE_MANAGERS} />}>
            <Route path={Nav.Submission} element={<ContractBuilderRoute />} />
          </Route>
          <Route element={<ProtectedRoutes authorizedRoles={ADMIN_ROLES} />}>
            <Route path={Nav.Template} element={<ContractBuilderRoute />} />
          </Route>
          <Route path={Nav.Endorsement} element={<ContractBuilderRoute />} />
          <Route path="*" element={<RedirectTo404 />} />
        </Route>
      </Route>,
    ),
  );
